import React, { useState } from 'react';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import ScrollToTop from '../components/ScrollToTop';
import SEO from '../components/SEO/SEO';

const TreatmentsPage = () => {
    // Categories to easily navigate
    const [activeTab, setActiveTab] = useState("All");
    const categories = ["All", "General", "Restorative", "Cosmetic", "Surgical"];

    const treatments = [
        {
            id: "general",
            category: "General",
            title: "General Dentistry",
            description: "Fundamental care for maintaining long-term oral health.",
            icon: "fas fa-tooth",
            items: [
                { name: "Checkups & Scaling", desc: "Routine examinations and piezoelectric cleaning in Kottawa to prevent gum disease." },
                { name: "White Fillings", desc: "Aesthetic composite restorations that match your natural tooth shade perfectly." },
                { name: "Extractions", desc: "Gentle, pain-free removal of non-restorable teeth with a focus on patient comfort." },
                { name: "Kids Dentistry", desc: "Friendly, fear-free dental care for children in Sri Lanka." }
            ]
        },
        {
            id: "restorative",
            category: "Restorative",
            title: "Restorative Care",
            description: "Rebuilding your smile's function and strength.",
            icon: "fas fa-tools",
            items: [
                { name: "Root Canal Treatment", desc: "Advanced endodontics to save infected teeth from extraction using modern technology." },
                { name: "Crowns & Bridges", desc: "High-quality zirconia and ceramic crowns in Sri Lanka for maximum durability." },
                { name: "Complete Dentures", desc: "Custom-fitted prosthetics (false teeth) for natural-looking tooth replacement." },
                { name: "Implant Dentures", desc: "Dentures secured by implants for superior stability and comfort." }
            ]
        },
        {
            id: "cosmetic",
            category: "Cosmetic",
            title: "Cosmetic & Orthodontics",
            description: "Designing the smile you've always wanted.",
            icon: "fas fa-magic",
            items: [
                { name: "Teeth Whitening", desc: "Professional laser bleaching in Kottawa for a brighter, more confident smile." },
                { name: "Ceramic Veneers", desc: "Thin, custom shells to correct discoloration and shape (Hollywood Smile)." },
                { name: "Clear Aligners", desc: "Invisible way to straighten teeth without metal braces (Orthodontics)." },
                { name: "Smile Design", desc: "Digital smile planning to visualize your perfect new look before treatment." }
            ]
        },
        {
            id: "surgical",
            category: "Surgical",
            title: "Surgical Procedures",
            description: "Expert solutions for complex dental needs.",
            icon: "fas fa-user-md",
            items: [
                { name: "Wisdom Teeth", desc: "Surgical removal of impacted third molars with minimal discomfort." },
                { name: "Dental Implants", desc: "Permanent, screw-retained replacements for missing teeth (Best Implant Prices)." },
                { name: "Bone Grafting", desc: "Building up bone structure for future implant placement success." },
                { name: "Oral Biopsy", desc: "Diagnostic tissue sampling for oral pathology and cancer screening." }
            ]
        }
    ];

    const filteredTreatments = activeTab === "All"
        ? treatments
        : treatments.filter(t => t.category === activeTab);

    return (
        <div className="bg-background-light dark:bg-background-dark text-slate-700 dark:text-slate-300 font-body transition-colors duration-300 min-h-screen">
            <SEO
                title="Dental Treatments - Implants, Veneers & More"
                description="Explore our comprehensive dental services including General Dentistry, Restorative Care, Cosmetic Dentistry, and Surgical Procedures at Smile Care."
                url="/treatments"
            />
            <ScrollToTop />
            <Navbar />

            {/* Header Section - Minimal & Clean */}
            <div className="pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-900 relative">
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                    <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-secondary/5 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-6 md:px-12 relative z-10 text-center">
                    <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs mb-4 block">
                        Comprehensive Care
                    </span>
                    <h1 className="font-display text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-6">
                        Curated Treatments
                    </h1>
                    <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto font-light leading-relaxed">
                        We combine art and science to provide a full spectrum of dental services.
                        Select a category below to explore our expertise.
                    </p>

                    {/* Filter Tabs */}
                    <div className="flex flex-wrap justify-center gap-3 mt-12">
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => setActiveTab(cat)}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${activeTab === cat
                                    ? "bg-primary text-white shadow-md transform scale-105"
                                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-primary hover:text-primary"
                                    }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <section className="py-16 bg-white dark:bg-slate-900">
                <div className="container mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 gap-12 max-w-5xl mx-auto">
                        {filteredTreatments.map((group) => (
                            <div key={group.id} className="group">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary text-xl">
                                        <i className={group.icon}></i>
                                    </div>
                                    <div>
                                        <h3 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                                            {group.title}
                                        </h3>
                                        <p className="text-sm text-slate-500 font-light">
                                            {group.description}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {group.items.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="flex flex-col p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-100 dark:hover:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 group-hover/item"
                                        >
                                            <div className="flex justify-between items-start mb-3">
                                                <h4 className="font-bold text-slate-900 dark:text-white text-lg">
                                                    {item.name}
                                                </h4>
                                                <i className="fas fa-arrow-right text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1 -ml-4"></i>
                                            </div>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-light">
                                                {item.desc}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                {/* Divider unless it's the last item */}
                                {group.id !== filteredTreatments[filteredTreatments.length - 1].id && (
                                    <div className="h-px bg-slate-100 dark:bg-slate-800 w-full my-12"></div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-24 max-w-3xl mx-auto">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8 md:p-10">
                            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                                {/* Icon */}
                                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center text-primary text-2xl shrink-0">
                                    <i className="fas fa-clipboard-list"></i>
                                </div>

                                {/* Text Content */}
                                <div className="flex-1 text-center md:text-left">
                                    <h3 className="font-display font-bold text-slate-900 dark:text-white text-xl mb-2">Custom Treatment Plans</h3>
                                    <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">We tailor every procedure to your specific smile goals, ensuring personalized care for optimal results.</p>
                                </div>

                                {/* CTA Button */}
                                <a
                                    href="tel:+94777880894"
                                    className="bg-primary hover:bg-primaryDark text-white px-8 py-3 rounded-full font-bold transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2 shrink-0"
                                >
                                    <i className="fas fa-phone-alt"></i>
                                    Book Consultation
                                </a>
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            <Footer />
            <FloatingWhatsApp />
        </div>
    );
};

export default TreatmentsPage;
